import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookContactDetail } from "~/shared/schemas/address-book-schema.ts";

/**
 * Parses the accessorials JSON string from an AddressBookContactDetail
 * and returns the matching Accessorial objects from the available accessorials list.
 *
 * @param detail - The contact detail containing the accessorials JSON string
 * @param allAccessorials - The full list of available accessorials
 * @returns Array of Accessorial objects that match the IDs in the detail
 */
export const parseAccessorialsFromDetail = (
	detail: AddressBookContactDetail | undefined,
	allAccessorials: Accessorial[] | undefined,
): Accessorial[] => {
	if (!detail?.accessorials || !allAccessorials) return [];

	try {
		// Parse the JSON string to get the array of accessorial IDs
		const accessorialIds = JSON.parse(detail.accessorials) as number[];
		if (Array.isArray(accessorialIds) && accessorialIds.length > 0) {
			return allAccessorials.filter((a) => accessorialIds.includes(a.id));
		}
	} catch (error) {
		console.error("Failed to parse accessorials:", error);
	}

	return [];
};

/**
 * Parses the accessorials field and returns an array of accessorial IDs.
 * Handles both JSON strings and arrays of numbers/objects.
 *
 * @param accessorials - The accessorials field (string, number[], or Accessorial[])
 * @returns Array of accessorial IDs
 */
export const getAccessorialIds = (
	accessorials: string | Accessorial[] | number[] | null | undefined,
): number[] => {
	if (Array.isArray(accessorials)) {
		if (accessorials.length > 0 && typeof accessorials[0] === "object") {
			return (accessorials as Accessorial[]).map((acc) => acc.id);
		}
		return accessorials as number[];
	}
	if (typeof accessorials === "string") {
		try {
			const parsed = JSON.parse(accessorials) as number[] | Accessorial[];
			if (Array.isArray(parsed)) {
				if (parsed.length > 0 && typeof parsed[0] === "object") {
					return (parsed as Accessorial[]).map((acc) => acc.id);
				}
				return parsed as number[];
			}
			return [];
		} catch {
			return [];
		}
	}
	return [];
};
